import React from 'react';
import { motion } from 'framer-motion';
import { Separator } from './ui/separator';
import { Github, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useMode } from '../contexts/ModeContext';

// Custom X (Twitter) icon component
const XIcon = ({ size = 24, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 4l11.733 16h4.267l-11.733 -16z" />
    <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { mode } = useMode();

  const footerLinks = {
    'Technology': [
      { name: 'MSML Overview', href: '#technology' },
      { name: 'Products', href: '#products' },
      { name: 'Markets', href: '#markets' },
      { name: 'Targets & Validation', href: '#targets' }
    ],
    'Company': mode === 'invest' 
      ? [
          { name: 'Roadmap', href: '#roadmap' },
          { name: 'Assets & IP', href: '#assets' },
          { name: 'Team', href: '#team' },
          { name: 'Investors', href: '#investment' }
        ]
      : [
          { name: 'Assets & IP', href: '#assets' }
        ],
    'Support': [
      { name: 'Contact', href: '#contact' },
      { name: 'Documentation', href: 'nda-required' },
      { name: 'API Reference', href: 'nda-required' },
      { name: 'ROI Calculator', href: '#roi' }
    ]
  };

  const socialLinks = [
    { icon: Github, href: '#', label: 'GitHub' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: XIcon, href: '#', label: 'X' },
    { icon: Mail, href: 'mailto:support@generalharmonics.com', label: 'Email' }
  ];

  const scrollToSection = (href) => {
    if (href === 'nda-required') {
      toast({
        title: "Document Access Restricted",
        description: "Requested document requires signed NDA, please contact the company through the correspondent contact form.",
        duration: 5000
      });
      return;
    }
    
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-700 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {Array.from({ length: 10 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-px h-20 bg-gradient-to-b from-cyan-400/20 to-transparent"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 0.5, 0],
              y: [0, -50]
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                <span className="text-cyan-400">General</span>
                <span className="text-blue-400">Harmonics</span>
              </h3>
              <p className="text-gray-400 text-sm">
                Pioneering the future of data intelligence with MSML technology. 
                Transforming AI, media, and telecommunications through revolutionary Intelligent Data Density Optimization Engine.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-2 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <MapPin size={14} className="text-cyan-400" />
                <span>Waterloo, Canada</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={14} className="text-cyan-400" />
                <span>+1 (519) 870 5831</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={14} className="text-cyan-400" />
                <span>support@generalharmonics.com</span>
              </div>
            </div>
          </motion.div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h4 className="text-white font-semibold mb-4">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <Separator className="my-8 bg-slate-700" />

        {/* Legal Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-slate-800/50 rounded-lg p-6 mb-8 border border-yellow-400/20"
        >
          <div className="text-xs text-gray-400 leading-relaxed">
            <p className="font-semibold text-yellow-400 mb-2">Forward-Looking Information & Target Disclosure:</p>
            <p>
              Certain statements on this site constitute forward-looking information and are based on current expectations and assumptions. 
              Actual results may differ materially. Figures labeled as "Targets" are objectives pending validation; methods and acceptance 
              thresholds are published before any results are claimed. This site is not an offer to sell or a solicitation of an offer to 
              buy any security. Past performance is not indicative of future results. General Harmonics makes no guarantees regarding the 
              performance, accuracy, or commercial viability of MSML technology until validation milestones are achieved and independently verified.
            </p>
          </div>
        </motion.div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          {/* Copyright */}
          <div className="text-gray-400 text-sm">
            © {currentYear} General Harmonics. All rights reserved. | MSML Technology Worldwide patent grants
          </div>

          {/* Social Links */}
          <div className="flex space-x-4">
            {socialLinks.map((social, index) => {
              const IconComponent = social.icon;
              return (
                <motion.a
                  key={social.label}
                  href={social.href}
                  className="text-gray-400 hover:text-cyan-400 transition-colors"
                  whileHover={{ scale: 1.2, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  title={social.label}
                >
                  <IconComponent size={20} />
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;